import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, defer, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { ILabel, LabelOrigin, LabelSeededFrom } from '../../../models/settings.models';
import {
  AddLabel,
  AddLabelError,
  AddLabelSuccess,
  DeleteLabel,
  FetchAllLabels,
  SetLabelsLoader,
  UpdateLabel,
  UpdateLabelError,
  UpdateLabelSuccess,
} from '../../../store/settings.actions';
import { LabelFormDialogComponent, LabelFormPayload } from './label-form-dialog/label-form-dialog.component';

/** Shown in place of a value the API did not send. Same token as the teams page. */
const EMPTY_STATE_VALUE = '--';

/** The i18n key for each origin, so the template does not build keys by concatenation. */
/** The backend sends a token; the wording lives here, beside the origin wording. */
const SEEDED_FROM_LABEL_KEYS: Record<LabelSeededFrom, string> = {
  product_category: 'settings.label_seeded_from_product_category',
  location_group: 'settings.label_seeded_from_company_group',
};

const ORIGIN_LABEL_KEYS: Record<LabelOrigin, string> = {
  manual: 'settings.label_origin_manual',
  provider_sync: 'settings.label_origin_provider_sync',
  erp: 'settings.label_origin_erp',
};

@Component({
  selector: 'app-labels',
  templateUrl: './labels.component.html',
  styleUrls: ['./labels.component.scss'],
})
export class LabelsComponent implements OnInit, OnDestroy {
  /**
   * Rendered inside the Smart Inbox settings tabs rather than on its own route.
   * Only the page header is suppressed -- that page already has one, and two
   * stacked headers is the bug this screen shipped with the first time.
   */
  @Input() embedded = false;

  /**
   * Filtering happens in the browser. `GET /v1/labels` has no search parameter and
   * the screen asks for the whole list in one read, so there is nothing to ask the
   * API for — the same reasoning as the teams page one nav item away.
   */
  readonly searchControl = new FormControl('');

  readonly labels$: Observable<ILabel[]> = this.store.select((state) => state.settings.allLabels);
  /**
   * `labelsLoading`, not the shared settings `loading`. It is read here only to keep
   * the empty state from flashing before the first response lands, and reading the
   * shared flag would make that flash depend on what another settings screen is doing.
   */
  readonly loading$: Observable<boolean> = this.store.select((state) => state.settings.labelsLoading);

  /** The rendered list. Row order is the API's; this only ever removes rows. */
  readonly filteredLabels$: Observable<ILabel[]> = combineLatest([
    this.labels$,
    // `defer` so a subscription created after the user has typed still starts from
    // the term currently in the box; a bare `startWith` would freeze the seed at ''.
    defer(() => this.searchControl.valueChanges.pipe(startWith(this.searchControl.value))),
  ]).pipe(
    map(([labels, term]) => {
      const needle = (term ?? '').trim().toLowerCase();
      if (!needle) return labels ?? [];
      return (labels ?? []).filter((label) => (label.name ?? '').toLowerCase().includes(needle));
    }),
  );

  readonly emptyStateValue = EMPTY_STATE_VALUE;

  private readonly destroy$ = new Subject<void>();
  private labelFormDialogLabel: ILabel | null = null;
  private labelFormDialogRef: MatDialogRef<LabelFormDialogComponent> | null = null;

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private actions$: Actions,
  ) {}

  /**
   * Refetch the tenant's labels. Public because importing a provider label
   * creates one, and the queue below this screen says so rather than the screen
   * having to watch for it.
   */
  reload(): void {
    this.store.dispatch(SetLabelsLoader({ isLoading: true }));
    this.store.dispatch(FetchAllLabels());
  }

  ngOnInit(): void {
    this.reload();

    this.actions$
      .pipe(ofType(AddLabelSuccess, UpdateLabelSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.closeLabelFormDialog());

    // Without this the save button spins for ever on a duplicate name or on the 400
    // a provider_sync name edit earns, and the dialog cannot be dismissed either
    // because `close()` refuses while it believes a save is in flight.
    this.actions$
      .pipe(ofType(AddLabelError, UpdateLabelError), takeUntil(this.destroy$))
      .subscribe(() => this.labelFormDialogRef?.componentInstance.setSubmitting(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True while the search box narrows the list, so the empty state can say which. */
  get hasSearchTerm(): boolean {
    return (this.searchControl.value ?? '').trim().length > 0;
  }

  editLabel(label?: ILabel): void {
    this.labelFormDialogLabel = label ?? null;
    this.openLabelFormDialog();
  }

  deleteLabel(label: ILabel): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (!result) return;

        this.store.dispatch(SetLabelsLoader({ isLoading: true }));
        this.store.dispatch(DeleteLabel({ id: label.id }));
      });
  }

  trackByLabelId(_: number, label: ILabel): number {
    return label.id;
  }


  originLabelKey(label: ILabel): string {
    return ORIGIN_LABEL_KEYS[label?.origin] ?? ORIGIN_LABEL_KEYS.manual;
  }

  /** Chip modifier. Falls back to `manual` exactly as the text does, so an
   *  unknown origin gets a styled chip rather than an unstyled bare word. */
  originClass(label: ILabel): string {
    return label?.origin in ORIGIN_LABEL_KEYS ? label.origin : 'manual';
  }

  /**
   * i18n key naming where a seeded label came from, or null.
   *
   * Null rather than a fallback key, unlike `originLabelKey`: every label has an
   * origin, but only a seeded one has a source, and inventing a default here
   * would caption manual labels with a provenance they do not have.
   */
  seededFromKey(label: ILabel): string | null {
    return label?.seeded_from ? SEEDED_FROM_LABEL_KEYS[label.seeded_from] ?? null : null;
  }

  private onLabelFormDialogSave(payload: LabelFormPayload): void {
    const selectedLabel = this.labelFormDialogLabel;

    if (selectedLabel?.id) {
      // `visible` and `eligible` are sent on every edit, `false` included: the PATCH
      // keys off presence, so omitting a `false` would leave the flag as it was and
      // make turning a label off look like it silently failed.
      this.store.dispatch(UpdateLabel({
        id: selectedLabel.id,
        label: this.buildUpdatePayload(selectedLabel, payload),
      }));
      return;
    }

    // `visible` and `eligible` are sent on create too. Omitting them let the server
    // apply its defaults (both true), so a label created with either switch off came
    // back on -- the edit path sent them and the create path did not, which is why
    // editing looked correct while creating silently ignored the switches.
    // No visible/eligible here: LabelCreateRequestDTO does not declare them and
    // BaseDTO's extra='ignore' drops them silently, so sending them would look
    // honoured and do nothing. A new label lands visible and eligible from the
    // model defaults; the switches are an edit-only control (GOV-01, via PATCH).
    this.store.dispatch(AddLabel({
      label: {
        name: payload.name,
        description: payload.description,
        color: payload.color,
      },
    }));
  }

  /**
   * A `provider_sync` label answers 400 to a name or description change and accepts a
   * colour or governance change on the same record. The form does not offer the first
   * two, so they must not be on the wire either — sending back the unchanged values
   * would still be a name edit as far as the backend's `touched_fields` is concerned,
   * and would fail an otherwise valid recolour.
   */
  private buildUpdatePayload(selectedLabel: ILabel, payload: LabelFormPayload) {
    if (selectedLabel.origin === 'provider_sync') {
      return {
        color: payload.color,
        visible: payload.visible,
        eligible: payload.eligible,
      };
    }

    return {
      name: payload.name,
      description: payload.description,
      color: payload.color,
      visible: payload.visible,
      eligible: payload.eligible,
    };
  }

  private openLabelFormDialog(): void {
    const pendingLabel = this.labelFormDialogLabel;
    this.closeLabelFormDialog();
    this.labelFormDialogLabel = pendingLabel;

    const ref = this.dialog.open(LabelFormDialogComponent, {
      data: { label: pendingLabel },
      panelClass: 'settings-user-form-dialog',
      disableClose: true,
    });
    this.labelFormDialogRef = ref;

    ref.componentInstance.save
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((payload) => this.onLabelFormDialogSave(payload));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.labelFormDialogRef === ref) {
          this.labelFormDialogRef = null;
          this.labelFormDialogLabel = null;
        }
      });
  }

  private closeLabelFormDialog(): void {
    this.labelFormDialogRef?.close();
    this.labelFormDialogRef = null;
    this.labelFormDialogLabel = null;
  }
}
